import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isBefore } from 'date-fns';

import { OfferDetailsType } from 'api/AppExternalController';
import { FEEDBACK_TYPES } from '@/constants';
import type { GetFeedbackForCoopResponse } from '@/app-package';
import {
  ShowingDetailsAppointmentType,
  ShowingDetailsListingType,
  ShowingDetailsPageUserType,
  ShowingDetailsProps,
} from '../types';
import { AUTO_ACTIONS, DENIED_STATUSES } from '../constants';

interface ShowingDetailsContextType extends Omit<ShowingDetailsProps, 'data'> {
  // data props
  appointment: ShowingDetailsAppointmentType;
  feedbackConfig?: GetFeedbackForCoopResponse & { id?: string };
  listing: ShowingDetailsListingType;
  listingAppointmentConfig: ShowingDetailsProps['data']['listingAppointmentConfig'];
  lockboxPhoto: string;
  offerDetails: OfferDetailsType;
  tourDetails?: ShowingDetailsProps['data']['tourDetails'];
  userType: ShowingDetailsPageUserType;
  // calculated or additional values
  autoAction?: AUTO_ACTIONS;
  denyReason?: string;
  feedbackRef: React.RefObject<HTMLDivElement>;
  isPassed: boolean;
  showFeedback: boolean;
  scrollToFeedback: () => void;
}
const ShowingDetailsContext = React.createContext<ShowingDetailsContextType>(
  {} as ShowingDetailsContextType,
);
export const ShowingDetailsContextProvider: React.FC<ShowingDetailsProps> = ({
  children,
  data,
  auth,
  fromTourPage,
  hasFeature,
  queryParams,
  fetchAppointment,
  fetchFeedback,
  setFeedbackConfig,
  tourFunctions,
}) => {
  const {
    appointment,
    feedbackConfig,
    listing,
    listingAppointmentConfig,
    lockboxPhotoURL,
    offerDetails,
    tourDetails,
    userType,
  } = data;
  const lockboxPhoto = lockboxPhotoURL ?? '';
  const [autoAction, setAutoAction] = useState<AUTO_ACTIONS | undefined>(
    undefined,
  );
  const feedbackRef = useRef<HTMLDivElement>(null);
  const isPassed = useMemo(
    () => isBefore(new Date(appointment.timeEnd), new Date()),
    [appointment.timeEnd],
  );
  const showFeedback = useMemo(() => {
    const { type, status, passed } = appointment;
    const isAcceptingFeedback = FEEDBACK_TYPES.includes(type);
    const isConfirmedAndInPast = status === 'confirm' && passed;
    return isAcceptingFeedback && isConfirmedAndInPast;
  }, [appointment]);
  const scrollToFeedback = () => {
    feedbackRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const getDenyReason = () => {
    const actionDetails = [...appointment.history]
      .reverse()
      .find(
        ({ status }) => status === 'cancel' || status === 'deny',
      )?.actionDetails;
    if (!actionDetails || typeof actionDetails === 'string') return undefined;
    return actionDetails.reason || actionDetails.message;
  };
  const denyReason = useMemo(() => {
    if (DENIED_STATUSES.find(status => status === appointment.status)) {
      return getDenyReason();
    }
  }, [appointment.history, appointment.status]);
  useEffect(() => {
    switch (queryParams.status) {
      case 'cancel':
        setAutoAction('cancel');
        break;
      case 'deny':
        setAutoAction('deny');
        break;
      case 'autoconfirm':
        setAutoAction('autoconfirm');
        break;
      case 'confirm':
        setAutoAction('confirm');
        break;
      default:
        break;
    }
    if (queryParams.submitFeedback === 'true') {
      scrollToFeedback();
    }
  }, [queryParams.status, queryParams.submitFeedback]);
  return (
    <ShowingDetailsContext.Provider
      value={{
        appointment,
        auth,
        autoAction,
        denyReason,
        feedbackConfig,
        feedbackRef,
        isPassed,
        fromTourPage,
        listing,
        listingAppointmentConfig,
        lockboxPhoto,
        offerDetails,
        queryParams,
        showFeedback,
        tourDetails,
        userType,
        fetchAppointment,
        fetchFeedback,
        hasFeature,
        scrollToFeedback,
        setFeedbackConfig,
        tourFunctions,
      }}
    >
      {children}
    </ShowingDetailsContext.Provider>
  );
};
export default ShowingDetailsContext;
